import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  Settings, Eye, DollarSign, PenLine, Shield, TrendingUp,
  Server, Activity, AlertCircle, CheckCircle, XCircle,
  RefreshCw, Trash2, ArrowLeft, Zap, Database, Clock,
  BarChart3, Power, PowerOff, AlertTriangle, Ban, Copy,
  MessageSquare, Users, MapPin, ImageIcon, Repeat, Bell, Mail, CreditCard,
  Sparkles, Lightbulb, Crown, TrendingUp as TrendUp, Rocket, Star, Heart,
  BadgeCheck, Phone, FileText, ShieldCheck, Flag, UserX, AlertOctagon, Lock, Unlock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fraud rule icons mapping
const FRAUD_RULE_ICONS = {
  'price_anomaly': DollarSign,
  'duplicate_content': Copy,
  'spam_keywords': Ban,
  'new_user_flood': Users,
  'contact_info_in_listing': MessageSquare,
  'suspicious_images': ImageIcon,
  'rapid_price_changes': Repeat,
  'location_mismatch': MapPin
};

const FRAUD_RULE_COLORS = {
  'high': 'text-red-600 bg-red-100',
  'medium': 'text-yellow-600 bg-yellow-100',
  'low': 'text-blue-600 bg-blue-100'
};

const AdminAIControlCenter = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [fraudRules, setFraudRules] = useState({});
  const [fraudStats, setFraudStats] = useState(null);
  const [updating, setUpdating] = useState({});
  const [activeTab, setActiveTab] = useState('features');
  
  // Regeneration and Membership Tones State
  const [regenSettings, setRegenSettings] = useState(null);
  const [tonesSettings, setTonesSettings] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  
  // Notification Settings State
  const [notifSettings, setNotifSettings] = useState(null);
  
  // Membership Pricing State
  const [pricingSettings, setPricingSettings] = useState(null);
  
  // AI Insights State
  const [insightsSettings, setInsightsSettings] = useState(null);
  
  // Boost Settings State
  const [boostSettings, setBoostSettings] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  
  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState(null);
  
  // Reviews Settings State
  const [reviewsSettings, setReviewsSettings] = useState(null);
  const [pendingReviews, setPendingReviews] = useState({ reviews: [], total: 0 });
  
  // Favorites Settings State
  const [favoritesSettings, setFavoritesSettings] = useState(null);
  const [favoritesStats, setFavoritesStats] = useState(null);
  
  // Verification Settings State
  const [verificationSettings, setVerificationSettings] = useState(null);
  const [verificationStats, setVerificationStats] = useState(null);
  const [pendingVerifications, setPendingVerifications] = useState({ verifications: [], total: 0 });
  
  // Moderation Settings State
  const [moderationSettings, setModerationSettings] = useState(null);
  const [moderationStats, setModerationStats] = useState(null);
  const [pendingReports, setPendingReports] = useState({ reports: [], total: 0 });
  const [blockedUsers, setBlockedUsers] = useState({ blocked_users: [], total: 0 });
  const [heldListings, setHeldListings] = useState({ listings: [], total: 0 });

  useEffect(() => {
    fetchSettings();
    fetchFraudRules();
    fetchFraudStats();
    fetchRegenSettings();
    fetchTonesSettings();
    fetchNotifSettings();
    fetchPricingSettings();
    fetchInsightsSettings();
    fetchBoostSettings();
    fetchPaymentSettings();
    fetchFavoritesSettings();
    fetchVerificationSettings();
    fetchModerationSettings();
    fetchReviewsSettings();
  }, []);
  
  const fetchRegenSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/regeneration-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegenSettings(data);
      }
    } catch (error) {
      console.error('Error fetching regeneration settings:', error);
    }
  };
  
  const fetchTonesSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/membership-tones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTonesSettings(data);
      }
    } catch (error) {
      console.error('Error fetching tones settings:', error);
    }
  };
  
  const fetchNotifSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifSettings(data);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };
  
  const fetchPricingSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/membership-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(data);
      }
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
    }
  };
  
  const fetchInsightsSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsightsSettings(data);
      }
    } catch (error) {
      console.error('Error fetching insights settings:', error);
    }
  };
  
  const toggleInsights = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsightsSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle AI Insights',
        variant: 'destructive'
      });
    }
  };
  
  const updateInsightsFrequency = async (frequency) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/settings/refresh-frequency?frequency=${frequency}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsightsSettings(prev => ({ ...prev, refresh_frequency: data.refresh_frequency }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update frequency',
        variant: 'destructive'
      });
    }
  };
  
  const updateMembershipGating = async (enabled, basicForAll, advancedPremiumOnly) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/settings/membership-gating?enabled=${enabled}&basic_for_all=${basicForAll}&advanced_premium_only=${advancedPremiumOnly}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsightsSettings(prev => ({ ...prev, membership_gating: data.membership_gating }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update membership gating',
        variant: 'destructive'
      });
    }
  };
  
  const toggleInsightType = async (insightType, enabled) => {
    setUpdating(prev => ({ ...prev, [`insight_${insightType}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/ai-insights/settings/insight-type/toggle`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ insight_type: insightType, enabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsightsSettings(prev => ({
          ...prev,
          insight_types: {
            ...prev?.insight_types,
            [insightType]: { ...prev?.insight_types?.[insightType], enabled: data.enabled }
          }
        }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle insight type',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`insight_${insightType}`]: false }));
    }
  };
  
  const fetchBoostSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/boost/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBoostSettings(data);
      }
    } catch (error) {
      console.error('Error fetching boost settings:', error);
    }
  };
  
  const toggleBoost = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/boost/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBoostSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle Boost feature',
        variant: 'destructive'
      });
    }
  };
  
  const toggleBoostPackage = async (packageKey) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/boost/admin/package/toggle/${packageKey}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBoostSettings(prev => ({
          ...prev,
          packages: {
            ...prev?.packages,
            [packageKey]: { ...prev?.packages?.[packageKey], enabled: data.enabled }
          }
        }));
        toast({
          title: 'Success',
          description: `Package ${data.enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle package',
        variant: 'destructive'
      });
    }
  };
  
  const updateBoostPackagePrice = async (packageKey, newPrice) => {
    setUpdating(prev => ({ ...prev, [`boost_${packageKey}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/boost/admin/package/price`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ package_key: packageKey, price: parseFloat(newPrice) })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBoostSettings(prev => ({
          ...prev,
          packages: {
            ...prev?.packages,
            [packageKey]: data.package
          }
        }));
        setEditingPackage(null);
        toast({
          title: 'Success',
          description: 'Package price updated'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update price',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`boost_${packageKey}`]: false }));
    }
  };
  
  const fetchPaymentSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/payments/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };
  
  const togglePayments = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/payments/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: `Payments ${data.enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle payments',
        variant: 'destructive'
      });
    }
  };
  
  const fetchReviewsSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/reviews/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviewsSettings(data);
      }
      
      // Fetch pending reviews count
      const pendingResponse = await fetch(`${API_URL}/api/reviews/admin/pending?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingReviews(pendingData);
      }
    } catch (error) {
      console.error('Error fetching reviews settings:', error);
    }
  };
  
  const toggleReviews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/reviews/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviewsSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle reviews',
        variant: 'destructive'
      });
    }
  };
  
  const updateReviewsSetting = async (key, value) => {
    setUpdating(prev => ({ ...prev, [`reviews_${key}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/reviews/admin/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviewsSettings(data.settings);
        toast({
          title: 'Success',
          description: 'Setting updated'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`reviews_${key}`]: false }));
    }
  };
  
  const approveReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/reviews/admin/${reviewId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPendingReviews(prev => ({
          ...prev,
          reviews: prev.reviews.filter(r => r.id !== reviewId),
          total: prev.total - 1
        }));
        toast({
          title: 'Success',
          description: 'Review approved'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve review',
        variant: 'destructive'
      });
    }
  };
  
  const rejectReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/reviews/admin/${reviewId}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPendingReviews(prev => ({
          ...prev,
          reviews: prev.reviews.filter(r => r.id !== reviewId),
          total: prev.total - 1
        }));
        toast({
          title: 'Success',
          description: 'Review rejected'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject review',
        variant: 'destructive'
      });
    }
  };
  
  const fetchFavoritesSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/favorites/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoritesSettings(data);
      }
      
      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/favorites/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setFavoritesStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching favorites settings:', error);
    }
  };
  
  const toggleFavorites = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/favorites/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoritesSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle favorites',
        variant: 'destructive'
      });
    }
  };
  
  const togglePriceAlerts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/favorites/admin/settings/toggle-price-alerts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoritesSettings(prev => ({ ...prev, price_alerts_enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle price alerts',
        variant: 'destructive'
      });
    }
  };
  
  const updateFavoritesSetting = async (key, value) => {
    setUpdating(prev => ({ ...prev, [`favorites_${key}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/favorites/admin/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoritesSettings(data.settings);
        toast({
          title: 'Success',
          description: 'Setting updated'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`favorites_${key}`]: false }));
    }
  };
  
  const fetchVerificationSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/verification/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationSettings(data);
      }
      
      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/verification/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setVerificationStats(statsData);
      }
      
      // Fetch pending verifications
      const pendingResponse = await fetch(`${API_URL}/api/verification/admin/pending?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingVerifications(pendingData);
      }
    } catch (error) {
      console.error('Error fetching verification settings:', error);
    }
  };
  
  const toggleVerification = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/verification/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle verification',
        variant: 'destructive'
      });
    }
  };
  
  const updateVerificationSetting = async (key, value) => {
    setUpdating(prev => ({ ...prev, [`verification_${key}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/verification/admin/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationSettings(data.settings);
        toast({
          title: 'Success',
          description: 'Setting updated'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`verification_${key}`]: false }));
    }
  };
  
  const approveVerification = async (verificationId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/verification/admin/${verificationId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPendingVerifications(prev => ({
          ...prev,
          verifications: prev.verifications.filter(v => v.id !== verificationId),
          total: prev.total - 1
        }));
        fetchVerificationSettings(); // Refresh stats
        toast({
          title: 'Success',
          description: 'Verification approved'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve verification',
        variant: 'destructive'
      });
    }
  };
  
  const rejectVerification = async (verificationId, reason) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/verification/admin/${verificationId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reason || 'Does not meet requirements' })
      });
      
      if (response.ok) {
        setPendingVerifications(prev => ({
          ...prev,
          verifications: prev.verifications.filter(v => v.id !== verificationId),
          total: prev.total - 1
        }));
        toast({
          title: 'Success',
          description: 'Verification rejected'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject verification',
        variant: 'destructive'
      });
    }
  };
  
  // Moderation Functions
  const fetchModerationSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModerationSettings(data);
      }
      
      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/moderation/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setModerationStats(statsData);
      }
      
      // Fetch pending reports
      const reportsResponse = await fetch(`${API_URL}/api/moderation/admin/reports?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reportsResponse.ok) {
        const reportsData = await reportsResponse.json();
        setPendingReports(reportsData);
      }
      
      // Fetch blocked users
      const blockedResponse = await fetch(`${API_URL}/api/moderation/admin/blocked-users?status=active&limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (blockedResponse.ok) {
        const blockedData = await blockedResponse.json();
        setBlockedUsers(blockedData);
      }
      
      // Fetch held listings
      const heldResponse = await fetch(`${API_URL}/api/moderation/admin/held-listings?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (heldResponse.ok) {
        const heldData = await heldResponse.json();
        setHeldListings(heldData);
      }
    } catch (error) {
      console.error('Error fetching moderation settings:', error);
    }
  };
  
  const toggleModeration = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModerationSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle moderation',
        variant: 'destructive'
      });
    }
  };
  
  const updateModerationSetting = async (key, value) => {
    setUpdating(prev => ({ ...prev, [`moderation_${key}`]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        setModerationSettings(data.settings);
        toast({
          title: 'Success',
          description: 'Setting updated'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`moderation_${key}`]: false }));
    }
  };
  
  const resolveReport = async (reportId, action) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/reports/${reportId}/resolve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, notes: '' })
      });
      
      if (response.ok) {
        setPendingReports(prev => ({
          ...prev,
          reports: prev.reports.filter(r => r.id !== reportId),
          total: prev.total - 1
        }));
        fetchModerationSettings();
        toast({
          title: 'Success',
          description: `Report ${action}`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve report',
        variant: 'destructive'
      });
    }
  };
  
  const unblockUser = async (userId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/unblock-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, reason: 'Admin unblock' })
      });
      
      if (response.ok) {
        setBlockedUsers(prev => ({
          ...prev,
          blocked_users: prev.blocked_users.filter(u => u.user_id !== userId),
          total: prev.total - 1
        }));
        fetchModerationSettings();
        toast({
          title: 'Success',
          description: 'User unblocked'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unblock user',
        variant: 'destructive'
      });
    }
  };
  
  const approveHeldListing = async (heldId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/held-listings/${heldId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setHeldListings(prev => ({
          ...prev,
          listings: prev.listings.filter(l => l.id !== heldId),
          total: prev.total - 1
        }));
        toast({
          title: 'Success',
          description: 'Listing approved'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve listing',
        variant: 'destructive'
      });
    }
  };
  
  const rejectHeldListing = async (heldId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/moderation/admin/held-listings/${heldId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Does not meet community guidelines' })
      });
      
      if (response.ok) {
        setHeldListings(prev => ({
          ...prev,
          listings: prev.listings.filter(l => l.id !== heldId),
          total: prev.total - 1
        }));
        toast({
          title: 'Success',
          description: 'Listing rejected'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject listing',
        variant: 'destructive'
      });
    }
  };
  
  const toggleRegeneration = async () => {
    setRegenLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/regeneration-settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegenSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle regeneration',
        variant: 'destructive'
      });
    } finally {
      setRegenLoading(false);
    }
  };
  
  const toggleMembershipTones = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/membership-tones/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTonesSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle membership tones',
        variant: 'destructive'
      });
    }
  };
  
  const toggleNotifications = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/notification-settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle notifications',
        variant: 'destructive'
      });
    }
  };
  
  const toggleEmailNotifications = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/notification-settings/email/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifSettings(prev => ({
          ...prev,
          email_notifications: { ...prev?.email_notifications, enabled: data.enabled }
        }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle email notifications',
        variant: 'destructive'
      });
    }
  };
  
  const toggleNotificationTriggers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/notification-settings/triggers/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifSettings(prev => ({
          ...prev,
          triggers: { ...prev?.triggers, enabled: data.enabled }
        }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle notification triggers',
        variant: 'destructive'
      });
    }
  };
  
  const toggleMembershipPricing = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/membership-pricing/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(prev => ({ ...prev, enabled: data.enabled }));
        toast({
          title: 'Success',
          description: data.message
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle membership pricing',
        variant: 'destructive'
      });
    }
  };
  
  const updatePlanPricing = async (plan, field, value) => {
    try {
      const token = localStorage.getItem('admin_token');
      const updateData = {
        plans: {
          [plan]: { [field]: parseInt(value) }
        }
      };
      
      const response = await fetch(`${API_URL}/api/admin/ai-settings/membership-pricing`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(data.settings);
        toast({
          title: 'Updated',
          description: `${plan} plan ${field} updated`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update pricing',
        variant: 'destructive'
      });
    }
  };
  
  const updateRegenPlan = async (plan, field, value) => {
    try {
      const token = localStorage.getItem('admin_token');
      const updateData = {};
      if (field === 'free_regens') {
        updateData.free_regens_per_plan = { [plan]: parseInt(value) };
      } else if (field === 'extra_cost') {
        updateData.extra_regen_cost_per_plan = { [plan]: parseInt(value) };
      }
      
      const response = await fetch(`${API_URL}/api/admin/ai-settings/regeneration-settings`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegenSettings(data.settings);
        toast({
          title: 'Updated',
          description: `${plan} plan ${field} updated`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive'
      });
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load AI settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect to server',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFraudRules = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai/fraud/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFraudRules(data.rules || {});
      }
    } catch (error) {
      console.error('Error fetching fraud rules:', error);
    }
  };

  const fetchFraudStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai/fraud/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFraudStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching fraud stats:', error);
    }
  };

  const toggleFraudRule = async (ruleId, enabled) => {
    setUpdating(prev => ({ ...prev, [`rule_${ruleId}`]: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai/fraud/rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled })
      });
      
      if (response.ok) {
        setFraudRules(prev => ({
          ...prev,
          [ruleId]: { ...prev[ruleId], enabled }
        }));
        toast({
          title: 'Success',
          description: `Fraud rule "${fraudRules[ruleId]?.name}" ${enabled ? 'enabled' : 'disabled'}`
        });
      } else {
        throw new Error('Failed to update rule');
      }
    } catch (error) {
      console.error('Error toggling fraud rule:', error);
      toast({
        title: 'Error',
        description: 'Failed to update fraud rule',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [`rule_${ruleId}`]: false }));
    }
  };

  const toggleAllFraudRules = async (enabled) => {
    setUpdating(prev => ({ ...prev, allFraudRules: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      
      // Toggle each rule
      for (const ruleId of Object.keys(fraudRules)) {
        await fetch(`${API_URL}/api/ai/fraud/rules/${ruleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ enabled })
        });
      }
      
      // Refresh rules
      await fetchFraudRules();
      
      toast({
        title: 'Success',
        description: `All fraud rules ${enabled ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      console.error('Error toggling all fraud rules:', error);
      toast({
        title: 'Error',
        description: 'Failed to update fraud rules',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, allFraudRules: false }));
    }
  };

  const toggleFeature = async (featureId, enabled) => {
    setUpdating(prev => ({ ...prev, [featureId]: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/features/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ feature_id: featureId, enabled })
      });
      
      if (response.ok) {
        setSettings(prev => ({
          ...prev,
          features: {
            ...prev.features,
            [featureId]: { ...prev.features[featureId], enabled }
          }
        }));
        toast({
          title: 'Success',
          description: `${featureId} ${enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast({
        title: 'Error',
        description: 'Failed to update feature',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [featureId]: false }));
    }
  };

  const toggleProvider = async (providerId, enabled) => {
    setUpdating(prev => ({ ...prev, [providerId]: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/providers/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ provider_id: providerId, enabled })
      });
      
      if (response.ok) {
        setSettings(prev => ({
          ...prev,
          providers: {
            ...prev.providers,
            [providerId]: { 
              ...prev.providers[providerId], 
              enabled,
              status: enabled ? 'active' : 'disabled'
            }
          }
        }));
        toast({
          title: 'Success',
          description: `${providerId} ${enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      console.error('Error toggling provider:', error);
      toast({
        title: 'Error',
        description: 'Failed to update provider',
        variant: 'destructive'
      });
    } finally {
      setUpdating(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const toggleAllAI = async (enabled) => {
    setUpdating(prev => ({ ...prev, global: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/global/toggle?enabled=${enabled}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSettings(prev => ({
          ...prev,
          global: { ...prev.global, ai_enabled: enabled }
        }));
        toast({
          title: enabled ? 'AI Enabled' : 'AI Disabled',
          description: `All AI features have been ${enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      console.error('Error toggling all AI:', error);
    } finally {
      setUpdating(prev => ({ ...prev, global: false }));
    }
  };

  const toggleAllFeatures = async (enabled) => {
    setUpdating(prev => ({ ...prev, allFeatures: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/features/toggle-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          features: data.features
        }));
        toast({
          title: 'Success',
          description: `All features ${enabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      console.error('Error toggling all features:', error);
    } finally {
      setUpdating(prev => ({ ...prev, allFeatures: false }));
    }
  };

  const clearCache = async () => {
    setUpdating(prev => ({ ...prev, cache: true }));
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/ai-settings/cache/clear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: 'Cache Cleared',
          description: 'AI response cache has been cleared'
        });
        fetchSettings(); // Refresh to get new metrics
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
    } finally {
      setUpdating(prev => ({ ...prev, cache: false }));
    }
  };

  const getFeatureIcon = (icon) => {
    const icons = {
      'eye': Eye,
      'dollar-sign': DollarSign,
      'pen-line': PenLine,
      'shield': Shield,
      'trending-up': TrendingUp
    };
    const Icon = icons[icon] || Settings;
    return <Icon className="w-5 h-5" />;
  };

  const getProviderStatusColor = (provider) => {
    if (!provider.enabled) return 'text-gray-400';
    if (!provider.api_key_set) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getProviderStatusBadge = (provider) => {
    if (!provider.enabled) return <Badge variant="outline" className="bg-gray-100">Disabled</Badge>;
    if (!provider.api_key_set) return <Badge className="bg-yellow-100 text-yellow-700">No API Key</Badge>;
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="w-6 h-6 text-purple-600" />
                  AI Control Center
                </h1>
                <p className="text-gray-500 text-sm">Manage AI features and providers</p>
              </div>
            </div>
            
            {/* Master Switch */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-600">Master Switch</span>
              <Button
                variant={settings?.global?.ai_enabled ? "default" : "outline"}
                className={settings?.global?.ai_enabled ? "bg-green-600 hover:bg-green-700" : ""}
                onClick={() => toggleAllAI(!settings?.global?.ai_enabled)}
                disabled={updating.global}
              >
                {settings?.global?.ai_enabled ? (
                  <>
                    <Power className="w-4 h-4 mr-2" />
                    AI Enabled
                  </>
                ) : (
                  <>
                    <PowerOff className="w-4 h-4 mr-2" />
                    AI Disabled
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Features Active</p>
                  <p className="text-2xl font-bold">
                    {settings?.features ? Object.values(settings.features).filter(f => f.enabled).length : 0}
                    <span className="text-gray-400 text-lg">/{Object.keys(settings?.features || {}).length}</span>
                  </p>
                </div>
                <Zap className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Providers Active</p>
                  <p className="text-2xl font-bold">
                    {settings?.providers ? Object.values(settings.providers).filter(p => p.enabled && p.api_key_set).length : 0}
                    <span className="text-gray-400 text-lg">/{Object.keys(settings?.providers || {}).length}</span>
                  </p>
                </div>
                <Server className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cache Hit Rate</p>
                  <p className="text-2xl font-bold">
                    {settings?.metrics?.pricing_cache?.hit_rate || 0}%
                  </p>
                </div>
                <Database className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Requests</p>
                  <p className="text-2xl font-bold">
                    {settings?.metrics?.pricing_cache?.total_requests || 0}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Features Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  AI Features
                </CardTitle>
                <CardDescription>Enable or disable individual AI features</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => toggleAllFeatures(true)}
                  disabled={updating.allFeatures}
                >
                  Enable All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => toggleAllFeatures(false)}
                  disabled={updating.allFeatures}
                >
                  Disable All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {settings?.features && Object.entries(settings.features).map(([featureId, feature]) => (
                <div 
                  key={featureId}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    feature.enabled 
                      ? 'border-purple-200 bg-purple-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${feature.enabled ? 'bg-purple-200 text-purple-700' : 'bg-gray-200 text-gray-500'}`}>
                        {getFeatureIcon(feature.icon)}
                      </div>
                      <div>
                        <h3 className="font-medium">{feature.name}</h3>
                        <p className="text-sm text-gray-500">{feature.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={feature.enabled}
                      onCheckedChange={(checked) => toggleFeature(featureId, checked)}
                      disabled={updating[featureId] || !settings?.global?.ai_enabled}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Providers Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-600" />
              AI Providers
            </CardTitle>
            <CardDescription>Manage AI service providers and fallback order</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {settings?.providers && Object.entries(settings.providers)
                .sort((a, b) => a[1].priority - b[1].priority)
                .map(([providerId, provider]) => (
                <div 
                  key={providerId}
                  className={`p-4 rounded-lg border transition-all ${
                    provider.enabled && provider.api_key_set
                      ? 'border-green-200 bg-green-50' 
                      : provider.enabled
                      ? 'border-yellow-200 bg-yellow-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        provider.enabled ? 'bg-blue-500' : 'bg-gray-400'
                      }`}>
                        {provider.priority}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{provider.name}</h3>
                          {getProviderStatusBadge(provider)}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          <span>Rate: {provider.rate_limit}/min</span>
                          <span>Daily: {provider.daily_limit}</span>
                          {provider.metrics && (
                            <span>Today: {provider.metrics.requests_today} requests</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {provider.metrics && (
                        <div className="text-right text-sm">
                          <div className="text-gray-500">
                            Success: {provider.metrics.successes} / {provider.metrics.successes + provider.metrics.failures}
                          </div>
                          <div className="text-gray-400">
                            Avg: {provider.metrics.avg_latency_ms?.toFixed(0) || 0}ms
                          </div>
                        </div>
                      )}
                      <Switch
                        checked={provider.enabled}
                        onCheckedChange={(checked) => toggleProvider(providerId, checked)}
                        disabled={updating[providerId] || !settings?.global?.ai_enabled}
                      />
                    </div>
                  </div>
                  
                  {provider.metrics && provider.enabled && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Daily Usage</span>
                        <span className="text-gray-600">
                          {provider.metrics.requests_today} / {provider.daily_limit}
                        </span>
                      </div>
                      <Progress 
                        value={(provider.metrics.requests_today / provider.daily_limit) * 100} 
                        className="h-2"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cache & Performance Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-green-600" />
              Cache & Performance
            </CardTitle>
            <CardDescription>Manage AI response caching</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Cache Status</span>
                  <Badge className={settings?.global?.cache_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                    {settings?.global?.cache_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="text-2xl font-bold">
                  {settings?.metrics?.pricing_cache?.cached_items || 0}
                  <span className="text-sm font-normal text-gray-500 ml-1">cached items</span>
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Hit Rate</span>
                  <Clock className="w-4 h-4 text-gray-400" />
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {settings?.metrics?.pricing_cache?.hit_rate || 0}%
                </div>
                <Progress 
                  value={settings?.metrics?.pricing_cache?.hit_rate || 0} 
                  className="h-2 mt-2"
                />
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">TTL</span>
                  <span className="text-sm text-gray-500">{settings?.global?.cache_ttl_hours || 6} hours</span>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={clearCache}
                  disabled={updating.cache}
                >
                  {updating.cache ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Clear Cache
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fraud Detection Rules Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  Fraud Detection Rules
                </CardTitle>
                <CardDescription>
                  Manage individual fraud detection patterns (8 rules)
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {fraudStats && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium text-red-600">{fraudStats.pending_review || 0}</span> pending review
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllFraudRules(true)}
                  disabled={updating.allFraudRules}
                >
                  Enable All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllFraudRules(false)}
                  disabled={updating.allFraudRules}
                >
                  Disable All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Fraud Stats Summary */}
            {fraudStats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-600">{fraudStats.pending_review || 0}</div>
                  <div className="text-xs text-yellow-700">Pending</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{fraudStats.by_risk_level?.high || 0}</div>
                  <div className="text-xs text-red-700">High Risk</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{fraudStats.today || 0}</div>
                  <div className="text-xs text-blue-700">Today</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{fraudStats.rules_enabled || 0}</div>
                  <div className="text-xs text-green-700">Rules Active</div>
                </div>
              </div>
            )}

            {/* Fraud Rules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(fraudRules).map(([ruleId, rule]) => {
                const RuleIcon = FRAUD_RULE_ICONS[ruleId] || AlertTriangle;
                const severityColor = FRAUD_RULE_COLORS[rule.severity] || FRAUD_RULE_COLORS.medium;
                
                return (
                  <div 
                    key={ruleId}
                    className={`p-4 border rounded-lg transition-all ${
                      rule.enabled 
                        ? 'bg-white border-gray-200 hover:border-red-300' 
                        : 'bg-gray-50 border-gray-100 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${rule.enabled ? 'bg-red-100' : 'bg-gray-200'}`}>
                          <RuleIcon className={`w-5 h-5 ${rule.enabled ? 'text-red-600' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{rule.name}</h4>
                            <Badge className={`text-xs ${severityColor}`}>
                              {rule.severity?.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={(checked) => toggleFraudRule(ruleId, checked)}
                        disabled={updating[`rule_${ruleId}`] || !settings?.features?.fraud_detection?.enabled}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {Object.keys(fraudRules).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No fraud rules configured</p>
              </div>
            )}

            {/* Note about master fraud switch */}
            {!settings?.features?.fraud_detection?.enabled && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-700">
                  Enable "AI Fraud Detection" in Features above to activate these rules.
                </span>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Membership & Regeneration Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              Membership & Regeneration Management
            </CardTitle>
            <CardDescription>
              Manage regeneration credits, bundles, and membership tone access
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Regeneration Master Switch */}
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${regenSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <RefreshCw className={`w-5 h-5 ${regenSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Regeneration Credits</h4>
                  <p className="text-sm text-gray-500">
                    {regenSettings?.enabled ? 'Users can regenerate ad content' : 'Regeneration disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={regenSettings?.enabled || false}
                onCheckedChange={toggleRegeneration}
                disabled={regenLoading}
              />
            </div>
            
            {/* Membership Tones Master Switch */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${tonesSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <PenLine className={`w-5 h-5 ${tonesSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Membership Tones</h4>
                  <p className="text-sm text-gray-500">
                    {tonesSettings?.enabled ? 'Tone access based on membership tier' : 'All tones disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={tonesSettings?.enabled || false}
                onCheckedChange={toggleMembershipTones}
              />
            </div>
            
            {/* Regeneration Credits per Plan */}
            {regenSettings?.enabled && (
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Free Regenerations per Plan
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {['free', 'basic', 'premium'].map(plan => (
                    <div key={plan} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize">{plan}</span>
                        <Badge variant={plan === 'premium' ? 'default' : plan === 'basic' ? 'secondary' : 'outline'}>
                          {plan === 'premium' ? '👑' : plan === 'basic' ? '⭐' : '🆓'}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-500">Free Regens</label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border rounded"
                            value={regenSettings?.free_regens_per_plan?.[plan] || 0}
                            onChange={(e) => updateRegenPlan(plan, 'free_regens', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Extra Cost (Rs.)</label>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm border rounded"
                            value={regenSettings?.extra_regen_cost_per_plan?.[plan] || 0}
                            onChange={(e) => updateRegenPlan(plan, 'extra_cost', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Regeneration Bundles */}
            {regenSettings?.enabled && regenSettings?.regeneration_bundles && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Regeneration Bundles
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {regenSettings.regeneration_bundles.map((bundle, idx) => (
                    <div key={idx} className="p-3 border rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{bundle.count}</p>
                      <p className="text-sm text-gray-500">regenerations</p>
                      <p className="text-lg font-semibold mt-1">Rs. {bundle.price}</p>
                      {bundle.discount && (
                        <Badge variant="secondary" className="mt-1">{bundle.discount} off</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Membership Tones per Plan */}
            {tonesSettings?.enabled && tonesSettings?.tones_per_plan && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <PenLine className="w-4 h-4" />
                  Tones per Membership Plan
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {['free', 'basic', 'premium'].map(plan => (
                    <div key={plan} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize">{plan}</span>
                        <Badge variant={plan === 'premium' ? 'default' : plan === 'basic' ? 'secondary' : 'outline'}>
                          {tonesSettings.tones_per_plan[plan]?.length || 0} tones
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(tonesSettings.tones_per_plan[plan] || []).map((tone, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {tone}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* System Limits */}
            {regenSettings?.enabled && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700 mb-2">System Limits</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Max Regens per Ad:</span>
                    <span className="ml-2 font-medium">{regenSettings.max_regens_per_ad || 10}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Daily Regen Limit:</span>
                    <span className="ml-2 font-medium">{regenSettings.daily_regen_limit || 50}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-600" />
              Notification Settings
            </CardTitle>
            <CardDescription>
              Manage in-app and email notification settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Notifications Master Switch */}
            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${notifSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Bell className={`w-5 h-5 ${notifSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">In-App Notifications</h4>
                  <p className="text-sm text-gray-500">
                    {notifSettings?.enabled ? 'Users receive in-app notifications' : 'In-app notifications disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={notifSettings?.enabled || false}
                onCheckedChange={toggleNotifications}
              />
            </div>
            
            {/* Email Notifications Switch */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${notifSettings?.email_notifications?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Mail className={`w-5 h-5 ${notifSettings?.email_notifications?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Email Notifications</h4>
                  <p className="text-sm text-gray-500">
                    {notifSettings?.email_notifications?.enabled ? 'Users receive email alerts' : 'Email alerts disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={notifSettings?.email_notifications?.enabled || false}
                onCheckedChange={toggleEmailNotifications}
                disabled={!notifSettings?.enabled}
              />
            </div>
            
            {/* Notification Triggers Switch */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${notifSettings?.triggers?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${notifSettings?.triggers?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Low Credit Alerts</h4>
                  <p className="text-sm text-gray-500">
                    {notifSettings?.triggers?.enabled ? 'Alerts triggered on low credits' : 'Credit alerts disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={notifSettings?.triggers?.enabled || false}
                onCheckedChange={toggleNotificationTriggers}
                disabled={!notifSettings?.enabled}
              />
            </div>
            
            {/* Trigger Settings */}
            {notifSettings?.enabled && notifSettings?.triggers?.enabled && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700 mb-3">Alert Thresholds</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="p-3 border rounded-lg bg-white">
                    <p className="text-gray-500 text-xs mb-1">Low Credit Threshold</p>
                    <p className="font-bold text-lg text-yellow-600">
                      {notifSettings?.triggers?.low_credit_threshold_percent || 20}%
                    </p>
                    <p className="text-xs text-gray-400">of total credits</p>
                  </div>
                  <div className="p-3 border rounded-lg bg-white">
                    <p className="text-gray-500 text-xs mb-1">Fixed Threshold</p>
                    <p className="font-bold text-lg text-orange-600">
                      {notifSettings?.triggers?.low_credit_fixed_threshold || 2}
                    </p>
                    <p className="text-xs text-gray-400">credits remaining</p>
                  </div>
                  <div className="p-3 border rounded-lg bg-white">
                    <p className="text-gray-500 text-xs mb-1">Low Regen Threshold</p>
                    <p className="font-bold text-lg text-purple-600">
                      {notifSettings?.triggers?.low_regen_threshold || 1}
                    </p>
                    <p className="text-xs text-gray-400">regens remaining</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Membership Pricing Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              Membership Pricing Management
            </CardTitle>
            <CardDescription>
              Manage membership plan pricing and discounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Pricing Management Master Switch */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${pricingSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <CreditCard className={`w-5 h-5 ${pricingSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Pricing Management</h4>
                  <p className="text-sm text-gray-500">
                    {pricingSettings?.enabled ? 'Admin can manage plan prices' : 'Pricing management disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={pricingSettings?.enabled || false}
                onCheckedChange={toggleMembershipPricing}
              />
            </div>
            
            {/* Pricing per Plan */}
            {pricingSettings?.enabled && (
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Plan Pricing
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {['free', 'basic', 'premium'].map(plan => (
                    <div key={plan} className={`p-4 border-2 rounded-lg ${
                      plan === 'premium' ? 'border-purple-300 bg-purple-50' :
                      plan === 'basic' ? 'border-blue-300 bg-blue-50' :
                      'border-gray-300 bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-lg font-bold capitalize">{plan}</span>
                        <Badge variant={plan === 'premium' ? 'default' : plan === 'basic' ? 'secondary' : 'outline'}>
                          {plan === 'premium' ? '👑 Premium' : plan === 'basic' ? '⭐ Basic' : '🆓 Free'}
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-600 font-medium">Price (Rs.)</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 text-sm border rounded-lg mt-1"
                            value={pricingSettings?.plans?.[plan]?.price || 0}
                            onChange={(e) => updatePlanPricing(plan, 'price', e.target.value)}
                            disabled={plan === 'free'}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 font-medium">Original Price (Rs.)</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 text-sm border rounded-lg mt-1"
                            value={pricingSettings?.plans?.[plan]?.original_price || 0}
                            onChange={(e) => updatePlanPricing(plan, 'original_price', e.target.value)}
                            disabled={plan === 'free'}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 font-medium">Discount (%)</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 text-sm border rounded-lg mt-1"
                            value={pricingSettings?.plans?.[plan]?.discount_percentage || 0}
                            onChange={(e) => updatePlanPricing(plan, 'discount_percentage', e.target.value)}
                            disabled={plan === 'free'}
                            min="0"
                            max="100"
                          />
                        </div>
                        {/* Display effective price */}
                        {plan !== 'free' && pricingSettings?.plans?.[plan]?.discount_percentage > 0 && (
                          <div className="p-2 bg-white rounded-lg text-center">
                            <span className="text-xs text-gray-500 line-through">
                              Rs. {pricingSettings?.plans?.[plan]?.original_price?.toLocaleString()}
                            </span>
                            <span className="text-green-600 text-xs ml-2">
                              {pricingSettings?.plans?.[plan]?.discount_percentage}% OFF
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              AI Insights Management
            </CardTitle>
            <CardDescription>
              Configure seller dashboard AI insights, refresh frequency, and membership gating
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${insightsSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Sparkles className={`w-5 h-5 ${insightsSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">AI Insights</h4>
                  <p className="text-sm text-gray-500">
                    {insightsSettings?.enabled ? 'Sellers receive AI-powered performance tips' : 'AI Insights disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={insightsSettings?.enabled || false}
                onCheckedChange={toggleInsights}
              />
            </div>
            
            {/* Refresh Frequency */}
            {insightsSettings?.enabled && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Refresh Frequency
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {['realtime', 'daily', 'weekly'].map((freq) => (
                    <button
                      key={freq}
                      onClick={() => updateInsightsFrequency(freq)}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        insightsSettings?.refresh_frequency === freq
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium capitalize">{freq}</p>
                      <p className="text-xs text-gray-500">
                        {freq === 'realtime' ? 'Every page load' : freq === 'daily' ? 'Once per day' : 'Once per week'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Membership Gating */}
            {insightsSettings?.enabled && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Membership Gating
                </h4>
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Enable Membership Gating</p>
                      <p className="text-xs text-gray-500">Restrict some insights to premium members</p>
                    </div>
                    <Switch
                      checked={insightsSettings?.membership_gating?.enabled || false}
                      onCheckedChange={(checked) => updateMembershipGating(
                        checked,
                        insightsSettings?.membership_gating?.basic_insights_for_all ?? true,
                        insightsSettings?.membership_gating?.advanced_for_premium_only ?? true
                      )}
                    />
                  </div>
                  
                  {insightsSettings?.membership_gating?.enabled && (
                    <>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div>
                          <p className="font-medium text-sm">Basic Insights for All</p>
                          <p className="text-xs text-gray-500">All users see basic performance tips</p>
                        </div>
                        <Switch
                          checked={insightsSettings?.membership_gating?.basic_insights_for_all || false}
                          onCheckedChange={(checked) => updateMembershipGating(
                            true,
                            checked,
                            insightsSettings?.membership_gating?.advanced_for_premium_only ?? true
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div>
                          <p className="font-medium text-sm">Advanced for Premium Only</p>
                          <p className="text-xs text-gray-500">Pricing & trend insights require premium</p>
                        </div>
                        <Switch
                          checked={insightsSettings?.membership_gating?.advanced_for_premium_only || false}
                          onCheckedChange={(checked) => updateMembershipGating(
                            true,
                            insightsSettings?.membership_gating?.basic_insights_for_all ?? true,
                            checked
                          )}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Insight Types */}
            {insightsSettings?.enabled && insightsSettings?.insight_types && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Insight Types
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(insightsSettings.insight_types).map(([typeId, typeConfig]) => (
                    <div
                      key={typeId}
                      className={`p-3 border rounded-lg ${
                        typeConfig.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium capitalize">
                            {typeId.replace(/_/g, ' ')}
                          </span>
                          <Badge variant={typeConfig.tier === 'advanced' ? 'default' : 'outline'} className="text-xs">
                            {typeConfig.tier}
                          </Badge>
                        </div>
                        <Switch
                          checked={typeConfig.enabled}
                          onCheckedChange={(checked) => toggleInsightType(typeId, checked)}
                          disabled={updating[`insight_${typeId}`]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Boost Optimizer Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-purple-600" />
              Boost Optimizer Management
            </CardTitle>
            <CardDescription>
              Configure boost packages, pricing, and AI recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${boostSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Rocket className={`w-5 h-5 ${boostSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Boost Feature</h4>
                  <p className="text-sm text-gray-500">
                    {boostSettings?.enabled ? 'Users can boost their ads for visibility' : 'Boost feature disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={boostSettings?.enabled || false}
                onCheckedChange={toggleBoost}
              />
            </div>
            
            {/* Boost Packages */}
            {boostSettings?.enabled && boostSettings?.packages && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Boost Packages (LKR)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(boostSettings.packages).map(([pkgKey, pkg]) => (
                    <div
                      key={pkgKey}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        pkg.enabled ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold">{pkg.name}</h5>
                        <Switch
                          checked={pkg.enabled}
                          onCheckedChange={() => toggleBoostPackage(pkgKey)}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Duration:</span>
                          <span className="font-medium">{pkg.duration_days} days</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Multiplier:</span>
                          <span className="font-medium">{pkg.multiplier}x</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Price:</span>
                          {editingPackage === pkgKey ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                defaultValue={pkg.price}
                                className="w-24 px-2 py-1 text-sm border rounded"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateBoostPackagePrice(pkgKey, e.target.value);
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingPackage(null);
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingPackage(null)}
                              >
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <button
                              className="font-bold text-purple-600 hover:underline"
                              onClick={() => setEditingPackage(pkgKey)}
                            >
                              Rs. {pkg.price?.toLocaleString()}
                            </button>
                          )}
                        </div>
                      </div>
                      {pkg.popular && (
                        <Badge className="mt-2 bg-blue-500">Most Popular</Badge>
                      )}
                      {pkg.best_value && (
                        <Badge className="mt-2 bg-green-500">Best Value</Badge>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">Click on a price to edit it. Press Enter to save or Escape to cancel.</p>
              </div>
            )}
            
            {/* AI Recommendations Toggle */}
            {boostSettings?.enabled && boostSettings?.ai_recommendations && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">AI Boost Recommendations</h4>
                  <p className="text-sm text-gray-500">Show AI-powered boost suggestions to sellers</p>
                </div>
                <Badge variant={boostSettings.ai_recommendations.enabled ? 'default' : 'secondary'}>
                  {boostSettings.ai_recommendations.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Gateway Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              Payment Gateway Management
            </CardTitle>
            <CardDescription>
              Control payment processing for memberships and boost purchases
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${paymentSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <CreditCard className={`w-5 h-5 ${paymentSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Payment Processing</h4>
                  <p className="text-sm text-gray-500">
                    {paymentSettings?.enabled ? 'Users can make payments via Stripe' : 'Payment processing disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={paymentSettings?.enabled || false}
                onCheckedChange={togglePayments}
              />
            </div>
            
            {/* Payment Info */}
            {paymentSettings?.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-800">Stripe Integration</h5>
                  <p className="text-sm text-blue-600 mt-1">International card payments</p>
                  <Badge className="mt-2 bg-green-500">Active</Badge>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="font-medium text-gray-800">Bank Transfer</h5>
                  <p className="text-sm text-gray-600 mt-1">Manual verification required</p>
                  <Badge className="mt-2 bg-green-500">Active</Badge>
                </div>
              </div>
            )}
            
            {/* Currency Info */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Currency</span>
              <span className="font-medium">{paymentSettings?.currency || 'LKR'} (Sri Lankan Rupee)</span>
            </div>
          </CardContent>
        </Card>

        {/* Reviews & Ratings Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Reviews & Ratings Management
            </CardTitle>
            <CardDescription>
              Control the seller review and rating system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${reviewsSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Star className={`w-5 h-5 ${reviewsSettings?.enabled ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Reviews System</h4>
                  <p className="text-sm text-gray-500">
                    {reviewsSettings?.enabled ? 'Users can review sellers' : 'Review system disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={reviewsSettings?.enabled || false}
                onCheckedChange={toggleReviews}
              />
            </div>
            
            {/* Review Settings */}
            {reviewsSettings?.enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Require Contact */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Require Contact</h5>
                      <p className="text-xs text-gray-500">Users must contact seller first</p>
                    </div>
                    <Switch
                      checked={reviewsSettings?.require_contact || false}
                      onCheckedChange={(val) => updateReviewsSetting('require_contact', val)}
                      disabled={updating['reviews_require_contact']}
                    />
                  </div>
                  
                  {/* Auto Approve */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Auto Approve</h5>
                      <p className="text-xs text-gray-500">Approve reviews automatically</p>
                    </div>
                    <Switch
                      checked={reviewsSettings?.auto_approve || false}
                      onCheckedChange={(val) => updateReviewsSetting('auto_approve', val)}
                      disabled={updating['reviews_auto_approve']}
                    />
                  </div>
                  
                  {/* Allow Edit */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Allow Edit</h5>
                      <p className="text-xs text-gray-500">Let users edit their reviews</p>
                    </div>
                    <Switch
                      checked={reviewsSettings?.allow_edit || false}
                      onCheckedChange={(val) => updateReviewsSetting('allow_edit', val)}
                      disabled={updating['reviews_allow_edit']}
                    />
                  </div>
                  
                  {/* Helpful Votes */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Helpful Votes</h5>
                      <p className="text-xs text-gray-500">Allow marking reviews as helpful</p>
                    </div>
                    <Switch
                      checked={reviewsSettings?.helpful_votes_enabled || false}
                      onCheckedChange={(val) => updateReviewsSetting('helpful_votes_enabled', val)}
                      disabled={updating['reviews_helpful_votes_enabled']}
                    />
                  </div>
                </div>
                
                {/* Edit Window */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-sm">Edit Window</h5>
                    <p className="text-xs text-gray-500">Hours after posting to allow edits</p>
                  </div>
                  <span className="font-semibold">{reviewsSettings?.edit_window_hours || 48} hours</span>
                </div>
                
                {/* Pending Reviews */}
                {!reviewsSettings?.auto_approve && pendingReviews.total > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        Pending Reviews ({pendingReviews.total})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {pendingReviews.reviews.slice(0, 3).map(review => (
                        <div key={review.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{review.reviewer_name}</span>
                              <div className="flex items-center">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 truncate">{review.content?.substring(0, 50)}...</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => approveReview(review.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => rejectReview(review.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Favorites & Wishlist Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Favorites & Wishlist Management
            </CardTitle>
            <CardDescription>
              Control the favorites system and price drop alerts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${favoritesSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Heart className={`w-5 h-5 ${favoritesSettings?.enabled ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Favorites System</h4>
                  <p className="text-sm text-gray-500">
                    {favoritesSettings?.enabled ? 'Users can save listings to favorites' : 'Favorites feature disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={favoritesSettings?.enabled || false}
                onCheckedChange={toggleFavorites}
              />
            </div>
            
            {/* Favorites Settings */}
            {favoritesSettings?.enabled && (
              <>
                {/* Price Alerts Toggle */}
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${favoritesSettings?.price_alerts_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Bell className={`w-5 h-5 ${favoritesSettings?.price_alerts_enabled ? 'text-orange-500' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium">Price Drop Alerts</h4>
                      <p className="text-sm text-gray-500">
                        {favoritesSettings?.price_alerts_enabled ? 'Users receive notifications when prices drop' : 'Price alerts disabled'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={favoritesSettings?.price_alerts_enabled || false}
                    onCheckedChange={togglePriceAlerts}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Email Notifications */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Email Notifications</h5>
                      <p className="text-xs text-gray-500">Send price alerts via email</p>
                    </div>
                    <Switch
                      checked={favoritesSettings?.email_notifications_enabled || false}
                      onCheckedChange={(val) => updateFavoritesSetting('email_notifications_enabled', val)}
                      disabled={updating['favorites_email_notifications_enabled'] || !favoritesSettings?.price_alerts_enabled}
                    />
                  </div>
                  
                  {/* In-App Notifications */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">In-App Notifications</h5>
                      <p className="text-xs text-gray-500">Show price alerts in app</p>
                    </div>
                    <Switch
                      checked={favoritesSettings?.in_app_notifications_enabled || false}
                      onCheckedChange={(val) => updateFavoritesSetting('in_app_notifications_enabled', val)}
                      disabled={updating['favorites_in_app_notifications_enabled'] || !favoritesSettings?.price_alerts_enabled}
                    />
                  </div>
                </div>
                
                {/* Settings Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Default Alert Threshold</h5>
                      <p className="text-xs text-gray-500">Price drop % to trigger alert</p>
                    </div>
                    <span className="font-semibold">{favoritesSettings?.default_alert_threshold || 10}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Max Favorites Per User</h5>
                      <p className="text-xs text-gray-500">Limit on saved items</p>
                    </div>
                    <span className="font-semibold">{favoritesSettings?.max_favorites_per_user || 100}</span>
                  </div>
                </div>
                
                {/* Stats */}
                {favoritesStats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-600">{favoritesStats.total_favorites || 0}</p>
                      <p className="text-sm text-blue-700">Total Favorites</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-orange-600">{favoritesStats.with_price_alerts || 0}</p>
                      <p className="text-sm text-orange-700">With Price Alerts</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-600">{favoritesStats.unique_users || 0}</p>
                      <p className="text-sm text-green-700">Active Users</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Seller Verification Badge Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-green-500" />
              Seller Verification Badge
            </CardTitle>
            <CardDescription>
              Manage seller verification system and trust badges
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${verificationSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <ShieldCheck className={`w-5 h-5 ${verificationSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Verification System</h4>
                  <p className="text-sm text-gray-500">
                    {verificationSettings?.enabled ? 'Sellers can verify their identity' : 'Verification system disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={verificationSettings?.enabled || false}
                onCheckedChange={toggleVerification}
              />
            </div>
            
            {/* Verification Settings */}
            {verificationSettings?.enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Phone Verification */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-blue-500" />
                      <div>
                        <h5 className="font-medium text-sm">Phone Verification</h5>
                        <p className="text-xs text-gray-500">Verify via OTP</p>
                      </div>
                    </div>
                    <Switch
                      checked={verificationSettings?.phone_verification_enabled || false}
                      onCheckedChange={(val) => updateVerificationSetting('phone_verification_enabled', val)}
                      disabled={updating['verification_phone_verification_enabled']}
                    />
                  </div>
                  
                  {/* ID Verification */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-500" />
                      <div>
                        <h5 className="font-medium text-sm">ID Verification</h5>
                        <p className="text-xs text-gray-500">Verify with ID document</p>
                      </div>
                    </div>
                    <Switch
                      checked={verificationSettings?.id_verification_enabled || false}
                      onCheckedChange={(val) => updateVerificationSetting('id_verification_enabled', val)}
                      disabled={updating['verification_id_verification_enabled']}
                    />
                  </div>
                  
                  {/* Auto Approve Phone */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Auto-Approve Phone</h5>
                      <p className="text-xs text-gray-500">Approve after OTP verification</p>
                    </div>
                    <Switch
                      checked={verificationSettings?.auto_approve_phone || false}
                      onCheckedChange={(val) => updateVerificationSetting('auto_approve_phone', val)}
                      disabled={updating['verification_auto_approve_phone']}
                    />
                  </div>
                  
                  {/* Show Badge on Listings */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium text-sm">Badge on Listings</h5>
                      <p className="text-xs text-gray-500">Show badge on ad cards</p>
                    </div>
                    <Switch
                      checked={verificationSettings?.show_badge_on_listings || false}
                      onCheckedChange={(val) => updateVerificationSetting('show_badge_on_listings', val)}
                      disabled={updating['verification_show_badge_on_listings']}
                    />
                  </div>
                </div>
                
                {/* Stats */}
                {verificationStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                      <p className="text-2xl font-bold text-yellow-600">{verificationStats.total_pending || 0}</p>
                      <p className="text-sm text-yellow-700">Pending</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-600">{verificationStats.total_approved || 0}</p>
                      <p className="text-sm text-green-700">Approved</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-600">{verificationStats.phone_verified_users || 0}</p>
                      <p className="text-sm text-blue-700">Phone Verified</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
                      <p className="text-2xl font-bold text-purple-600">{verificationStats.id_verified_users || 0}</p>
                      <p className="text-sm text-purple-700">ID Verified</p>
                    </div>
                  </div>
                )}
                
                {/* Pending Verifications */}
                {pendingVerifications.total > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        Pending Verifications ({pendingVerifications.total})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {pendingVerifications.verifications.slice(0, 3).map(v => (
                        <div key={v.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{v.user?.name || 'Unknown User'}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                v.type === 'phone' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {v.type === 'phone' ? 'Phone' : 'ID'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">
                              {v.type === 'phone' ? v.phone_number : `${v.id_type?.toUpperCase()}: ${v.id_number}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => approveVerification(v.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => rejectVerification(v.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Moderation & Safety Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Moderation & Safety
            </CardTitle>
            <CardDescription>
              Report system, content moderation, and user blocking
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${moderationSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Shield className={`w-5 h-5 ${moderationSettings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Moderation System</h4>
                  <p className="text-sm text-gray-500">
                    {moderationSettings?.enabled ? 'All moderation features active' : 'Moderation system disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={moderationSettings?.enabled || false}
                onCheckedChange={toggleModeration}
              />
            </div>
            
            {moderationSettings?.enabled && (
              <>
                {/* Moderation Sub-Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Reports System */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-orange-500" />
                      <div>
                        <h5 className="font-medium text-sm">User Reports</h5>
                        <p className="text-xs text-gray-500">Allow users to report others</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.reports_enabled || false}
                      onCheckedChange={(val) => updateModerationSetting('reports_enabled', val)}
                      disabled={updating['moderation_reports_enabled']}
                    />
                  </div>
                  
                  {/* Language Moderation */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-blue-500" />
                      <div>
                        <h5 className="font-medium text-sm">Language Filter</h5>
                        <p className="text-xs text-gray-500">Auto-detect inappropriate content</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.language_moderation_enabled || false}
                      onCheckedChange={(val) => updateModerationSetting('language_moderation_enabled', val)}
                      disabled={updating['moderation_language_moderation_enabled']}
                    />
                  </div>
                  
                  {/* Auto Warn */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      <div>
                        <h5 className="font-medium text-sm">Auto Warning</h5>
                        <p className="text-xs text-gray-500">Warn users automatically</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.auto_warn_inappropriate || false}
                      onCheckedChange={(val) => updateModerationSetting('auto_warn_inappropriate', val)}
                      disabled={updating['moderation_auto_warn_inappropriate']}
                    />
                  </div>
                  
                  {/* Illegal Product Detection */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-red-500" />
                      <div>
                        <h5 className="font-medium text-sm">Illegal Products</h5>
                        <p className="text-xs text-gray-500">Auto-detect prohibited items</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.illegal_product_detection_enabled || false}
                      onCheckedChange={(val) => updateModerationSetting('illegal_product_detection_enabled', val)}
                      disabled={updating['moderation_illegal_product_detection_enabled']}
                    />
                  </div>
                  
                  {/* Auto Hold Listings */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-purple-500" />
                      <div>
                        <h5 className="font-medium text-sm">Auto-Hold Listings</h5>
                        <p className="text-xs text-gray-500">Hold suspicious ads for review</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.auto_hold_suspicious_listings || false}
                      onCheckedChange={(val) => updateModerationSetting('auto_hold_suspicious_listings', val)}
                      disabled={updating['moderation_auto_hold_suspicious_listings']}
                    />
                  </div>
                  
                  {/* Age Restriction */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <UserX className="w-4 h-4 text-indigo-500" />
                      <div>
                        <h5 className="font-medium text-sm">Age Restriction</h5>
                        <p className="text-xs text-gray-500">Flag age-restricted products</p>
                      </div>
                    </div>
                    <Switch
                      checked={moderationSettings?.age_restriction_enabled || false}
                      onCheckedChange={(val) => updateModerationSetting('age_restriction_enabled', val)}
                      disabled={updating['moderation_age_restriction_enabled']}
                    />
                  </div>
                </div>
                
                {/* Config Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Auto-block after reports</span>
                    <span className="font-semibold">{moderationSettings?.auto_block_after_reports || 5}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Warnings before block</span>
                    <span className="font-semibold">{moderationSettings?.warnings_before_block || 3}</span>
                  </div>
                </div>
                
                {/* Stats */}
                {moderationStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-orange-600">{moderationStats.pending_reports || 0}</p>
                      <p className="text-sm text-orange-700">Pending Reports</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-center">
                      <p className="text-2xl font-bold text-red-600">{moderationStats.active_blocks || 0}</p>
                      <p className="text-sm text-red-700">Blocked Users</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
                      <p className="text-2xl font-bold text-purple-600">{moderationStats.held_listings || 0}</p>
                      <p className="text-sm text-purple-700">Held Listings</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                      <p className="text-2xl font-bold text-yellow-600">{moderationStats.total_warnings || 0}</p>
                      <p className="text-sm text-yellow-700">Total Warnings</p>
                    </div>
                  </div>
                )}
                
                {/* Pending Reports Queue */}
                {pendingReports.total > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <Flag className="w-4 h-4 text-orange-500" />
                        Pending Reports ({pendingReports.total})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {pendingReports.reports.slice(0, 3).map(report => (
                        <div key={report.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{report.reported_user?.name || 'Unknown'}</span>
                              <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">
                                {report.category}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 truncate">{report.reason}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-yellow-600 hover:bg-yellow-50"
                              onClick={() => resolveReport(report.id, 'warn')}
                            >
                              Warn
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => resolveReport(report.id, 'block')}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-600 hover:bg-gray-100"
                              onClick={() => resolveReport(report.id, 'dismiss')}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Blocked Users */}
                {blockedUsers.total > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <UserX className="w-4 h-4 text-red-500" />
                        Blocked Users ({blockedUsers.total})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {blockedUsers.blocked_users.slice(0, 3).map(block => (
                        <div key={block.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex-1">
                            <span className="font-medium text-sm">{block.user?.name || block.user_id}</span>
                            <p className="text-xs text-gray-600">{block.reason}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => unblockUser(block.user_id)}
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Unblock
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Held Listings */}
                {heldListings.total > 0 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <AlertOctagon className="w-4 h-4 text-purple-500" />
                        Held Listings ({heldListings.total})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {heldListings.listings.slice(0, 3).map(listing => (
                        <div key={listing.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div className="flex-1">
                            <span className="font-medium text-sm">{listing.title}</span>
                            <p className="text-xs text-gray-600">
                              Issues: {listing.issues?.map(i => i.matched).join(', ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => approveHeldListing(listing.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => rejectHeldListing(listing.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Last Updated */}
        <div className="text-center text-sm text-gray-500">
          Last updated: {settings?.updated_at ? new Date(settings.updated_at).toLocaleString() : 'Never'}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={fetchSettings}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAIControlCenter;
